import { Link } from "wouter";
import { motion } from "framer-motion";
import { Shield, Mail, ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-muted/30">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link href="/">
            <a className="font-serif text-2xl font-semibold tracking-tight" data-testid="link-home">
              <span className="text-primary">Life</span> Itself Studio
            </a>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link href="/">
              <a className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </a>
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 text-primary" />
              <h1 className="font-serif text-4xl md:text-5xl font-semibold" data-testid="heading-privacy">
                Privacy Policy
              </h1>
            </div>

            <p className="text-muted-foreground mb-8">
              Last Updated: January 2, 2026
            </p>

            <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Life Itself Studio ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our website at lifeitself.studio, place an order, or interact with our services. By using our services, you consent to the practices described in this policy.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Information We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We collect various types of information to provide and improve our services:
                </p>

                <h3 className="font-medium text-lg mb-2">Personal Information You Provide</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                  <li><strong>Contact Information:</strong> Name, email address, phone number, and delivery address</li>
                  <li><strong>Payment Information:</strong> Credit/debit card details (processed securely by Stripe)</li>
                  <li><strong>Order Details:</strong> Terrarium preferences, biome selections, delivery date preferences, and special instructions</li>
                  <li><strong>Account Information:</strong> If you create an account, we store your login credentials and order history</li>
                  <li><strong>Communications:</strong> Any messages, feedback, or inquiries you send us</li>
                  <li><strong>Waitlist Information:</strong> If you join our expansion waitlist, we collect your name, email, state, phone number, and biome preferences</li>
                </ul>

                <h3 className="font-medium text-lg mb-2">Information Collected Automatically</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns, referral sources</li>
                  <li><strong>Location Data:</strong> General geographic location based on IP address</li>
                  <li><strong>Cookies and Tracking Technologies:</strong> See our Cookie Usage section below</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">How We Use Your Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use your personal information for the following purposes:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Order Fulfillment:</strong> Processing and delivering your terrarium orders</li>
                  <li><strong>Customer Service:</strong> Responding to inquiries, providing support, and handling complaints</li>
                  <li><strong>Payment Processing:</strong> Completing transactions securely through our payment provider</li>
                  <li><strong>Communication:</strong> Sending order confirmations, shipping updates, and delivery notifications</li>
                  <li><strong>Marketing:</strong> With your consent, sending promotional emails about new products, special offers, and workshops</li>
                  <li><strong>Service Improvement:</strong> Analyzing usage patterns to enhance our website and offerings</li>
                  <li><strong>Legal Compliance:</strong> Meeting our legal obligations and protecting our rights</li>
                  <li><strong>Fraud Prevention:</strong> Detecting and preventing fraudulent transactions</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Legal Basis for Processing (GDPR)</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  For users in the European Economic Area (EEA), we process your personal data based on the following legal grounds:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Contract Performance:</strong> Processing necessary to fulfill our agreement with you (e.g., delivering your order)</li>
                  <li><strong>Legitimate Interests:</strong> Processing for our business interests, such as fraud prevention, improving our services, and marketing (where not overridden by your rights)</li>
                  <li><strong>Consent:</strong> Where you have given explicit consent (e.g., marketing emails)</li>
                  <li><strong>Legal Obligation:</strong> Processing required to comply with applicable laws</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Third-Party Services</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We share your information with trusted third-party service providers who assist us in operating our business:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Stripe:</strong> Secure payment processing. Stripe handles all payment card data and is PCI-DSS compliant. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Privacy Policy</a></li>
                  <li><strong>SendGrid:</strong> Email delivery for order confirmations, shipping updates, and marketing communications. <a href="https://www.twilio.com/en-us/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">SendGrid Privacy Policy</a></li>
                  <li><strong>Sentry:</strong> Error monitoring and performance tracking to improve our website. <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Sentry Privacy Policy</a></li>
                  <li><strong>Hosting Provider:</strong> Our website is hosted on secure servers that may process your data</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  These third parties are contractually obligated to protect your data and may only use it for the specific services they provide to us.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We retain your personal information for as long as necessary to fulfill the purposes described in this policy:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Order Records:</strong> 7 years for tax and legal compliance purposes</li>
                  <li><strong>Customer Accounts:</strong> Until you request deletion or after 3 years of inactivity</li>
                  <li><strong>Marketing Preferences:</strong> Until you unsubscribe or withdraw consent</li>
                  <li><strong>Waitlist Data:</strong> Until we expand to your area or you request removal, whichever comes first</li>
                  <li><strong>Website Analytics:</strong> Aggregated data retained indefinitely; personal identifiers deleted after 26 months</li>
                  <li><strong>Customer Support Records:</strong> 3 years from last interaction</li>
                </ul>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Your Rights</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Depending on your location, you may have the following rights regarding your personal information:
                </p>

                <h3 className="font-medium text-lg mb-2">For All Users</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
                  <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                </ul>

                <h3 className="font-medium text-lg mb-2">GDPR Rights (EEA Users)</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                  <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                  <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                  <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw previously given consent at any time</li>
                  <li><strong>Lodge Complaint:</strong> File a complaint with your local data protection authority</li>
                </ul>

                <h3 className="font-medium text-lg mb-2">CCPA Rights (California Residents)</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Know:</strong> Right to know what personal information we collect, use, and disclose</li>
                  <li><strong>Delete:</strong> Right to request deletion of your personal information</li>
                  <li><strong>Opt-Out of Sale:</strong> We do not sell your personal information</li>
                  <li><strong>Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  To exercise any of these rights, please contact us at <a href="mailto:privacy@lifeitself.studio" className="text-primary hover:underline">privacy@lifeitself.studio</a>. We will respond within 30 days (or sooner as required by law).
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Cookie Usage</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use cookies and similar tracking technologies to enhance your experience on our website:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for basic website functionality (shopping cart, checkout process)</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                  <li><strong>Performance Cookies:</strong> Monitor website performance and error tracking</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                  <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track ad campaign performance</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  You can manage cookie preferences at any time through your browser settings.
                  Note that disabling certain cookies may affect website functionality.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Security Measures</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We implement robust security measures to protect your personal information:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Encryption:</strong> All data transmitted between your browser and our servers is encrypted using TLS/SSL</li>
                  <li><strong>Secure Payment Processing:</strong> Payment information is handled exclusively by PCI-DSS compliant Stripe</li>
                  <li><strong>Access Controls:</strong> Limited employee access to personal information on a need-to-know basis</li>
                  <li><strong>Regular Security Audits:</strong> Periodic reviews of our security practices and infrastructure</li>
                  <li><strong>Secure Storage:</strong> Personal data stored on encrypted servers with regular backups</li>
                  <li><strong>Incident Response:</strong> Procedures in place to detect, report, and respond to data breaches</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  While we strive to protect your information, no method of transmission over the internet is 100% secure. We encourage you to use strong passwords and keep your login credentials confidential.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are not directed to individuals under 16 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child under 16, we will take steps to delete such information promptly.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">International Data Transfers</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Life Itself Studio is based in the United States. If you access our services from outside the United States, please be aware that your information may be transferred to, stored, and processed in the United States. By using our services, you consent to this transfer. We ensure appropriate safeguards are in place for international data transfers in compliance with applicable data protection laws.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Do Not Track</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Some browsers include a "Do Not Track" (DNT) feature. We currently do not respond to DNT signals, as there is no industry-wide standard for compliance. We continue to monitor developments in this area.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new policy on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.
                </p>
              </section>

              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
                </p>
                <div className="bg-muted/50 rounded-lg p-6">
                  <p className="font-medium mb-2">Life Itself Studio</p>
                  <p className="text-muted-foreground">Denver, Colorado</p>
                  <p className="text-muted-foreground flex items-center gap-2 mt-2">
                    <Mail className="h-4 w-4" />
                    <a href="mailto:privacy@lifeitself.studio" className="text-primary hover:underline">privacy@lifeitself.studio</a>
                  </p>
                  <p className="text-muted-foreground mt-2">
                    General inquiries: <a href="mailto:hello@lifeitself.studio" className="text-primary hover:underline">hello@lifeitself.studio</a>
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
